import React, { useState, useRef } from 'react';
import { FileText, Upload, Trash2, Loader2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  addFileByCustomerCompanyManager,
  removeFileByCustomerCompanyManager,
} from '../../../../services/staffingService';

const FILE_CATEGORIES = ['SLA', 'Layout Plan', 'Contract', 'Other'];

const DocumentsTab = ({ customerCompany, setCustomerCompany, customerCompanyId }) => {
  const fileInputRef   = useRef(null);
  const [uploading,    setUploading]    = useState(false);
  const [fileCategory, setFileCategory] = useState('');
  const [pendingFile,  setPendingFile]  = useState(null);

  const files = customerCompany?.files || [];

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingFile(file);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!pendingFile || !fileCategory) return toast.error('Select a file and category');
    setUploading(true);
    try {
      const resp = await addFileByCustomerCompanyManager(customerCompanyId, pendingFile, fileCategory);
      const newFile = resp?.data || { fileType: fileCategory, fileName: pendingFile.name, fileUrl: '', uploadDate: new Date() };
      setCustomerCompany(prev => ({ ...prev, files: [...(prev.files || []), newFile] }));
      setPendingFile(null);
      setFileCategory('');
      toast.success('File uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      await removeFileByCustomerCompanyManager(customerCompanyId, fileId);
      setCustomerCompany(prev => ({ ...prev, files: (prev.files || []).filter(f => f._id !== fileId) }));
      toast.success('File removed');
    } catch {
      toast.error('Failed to remove file');
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Upload Document</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 rounded-lg border-2 border-dashed border-gray-200 px-4 py-2.5 text-sm text-gray-500 hover:border-[#404880]/40 hover:text-[#404880] transition-colors"
          >
            <Upload size={14} />
            {pendingFile ? pendingFile.name : 'Choose file…'}
          </button>
          <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg" className="hidden" onChange={handleFileChange} />

          <select
            value={fileCategory}
            onChange={e => setFileCategory(e.target.value)}
            className="w-48 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#404880]"
          >
            <option value="">Category…</option>
            {FILE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <button
            onClick={handleUpload}
            disabled={!pendingFile || !fileCategory || uploading}
            className="flex items-center gap-1.5 rounded-lg bg-[#404880] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            Upload
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white">
        {files.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText size={28} className="text-gray-300 mb-2" />
            <p className="text-sm text-gray-500">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {files.map((file) => (
              <div key={file._id} className="flex items-center gap-3 px-5 py-3.5">
                <FileText size={16} className="flex-shrink-0 text-[#404880]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.fileName}</p>
                  <p className="text-xs text-gray-400">{file.fileType}</p>
                </div>
                {file.fileUrl && (
                  <a href={file.fileUrl} target="_blank" rel="noreferrer"
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-[#404880] transition-colors">
                    <ExternalLink size={14} />
                  </a>
                )}
                <button onClick={() => handleDelete(file._id)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab;
